export type QuizLocale = "en-US" | "ja-JP" | "ko-KR";

export interface QuizUiStrings {
  pageTitle: string;
  loadingQuiz: string;
  genericLoadError: string;
  genericSaveError: string;
  genericPublishError: string;
  genericPublishSuccess: string;
  genericCopied: string;
  landingTitle: string;
  landingDescription: string;
  startNewQuiz: string;
  startNewQuizBody: string;
  sipTogetherAction: string;
  sipTogetherDescription: string;
  enterCodeTitle: string;
  enterCodeBody: string;
  enterCodeLabel: string;
  enterCodePlaceholder: string;
  createQuizAction: string;
  joinQuizAction: string;
  signInToCreate: string;
  signInToJoin: string;
  authContinueTitle: string;
  authContinueBody: string;
  joinHostedByLabel: string;
  joinClosed: string;
  invalidCode: string;
  lobbyTitle: string;
  lobbyDescription: string;
  codeLabel: string;
  inviteEmailsTitle: string;
  inviteEmailsDescription: string;
  inviteEmailsLabel: string;
  inviteEmailsPlaceholder: string;
  inviteEmailsSendAction: string;
  inviteEmailsInvalid: string;
  inviteEmailsTooMany: string;
  inviteEmailsSuccess: string;
  inviteEmailsPartial: string;
  joinedPlayersTitle: string;
  shareHeading: string;
  shareTitle: string;
  shareText: string;
  copyCodeAction: string;
  startLobbyAction: string;
  startNeedsOtherPlayer: string;
  waitingForCreator: string;
  noPlayersYet: string;
  part1IssuesTitle: string;
  part1IssuesDescription: string;
  issueModalActionLabel: string;
  issueModalSubscribeLabel: string;
  issueModalSubscribeDescription: string;
  issueModalEmptyTitle: string;
  issueModalEmptySubtitle: string;
  searchIssuePlaceholder: string;
  selectedIssuesTitle: string;
  noIssuesYet: string;
  needThreeIssues: string;
  continueToTeabateAction: string;
  continueToWaitingAction: string;
  sentimentNegative: string;
  sentimentNeutral: string;
  sentimentPositive: string;
  removeIssueAction: string;
  part1TeabateTitle: string;
  part1TeabateDescription: string;
  perspectiveCreateButtonText: string;
  perspectiveCreateButtonTooltip: string;
  perspectiveCloseLabel: string;
  selectIssueFirstText: string;
  chooseRankedIssueLabel: string;
  issuePickerNoResults: string;
  selectedTeabatesTitle: string;
  existingTeabatesTitle: string;
  existingTeabatesHelp: string;
  noExistingTeabates: string;
  needAtLeastOneTeabate: string;
  maxTeabatesReached: string;
  addMoreTeabatesAction: string;
  createNewTeabateAction: string;
  useSelectedTeabateAction: string;
  createClaimLabel: string;
  createArgumentLabel: string;
  createDimensionLabel: string;
  createDimensionPlaceholder: string;
  createAgreeLabel: string;
  createDisagreeLabel: string;
  dimensionNationalism: string;
  dimensionCosmopolitanism: string;
  dimensionTheocracy: string;
  dimensionSecularism: string;
  dimensionAuthority: string;
  dimensionLiberty: string;
  dimensionCapitalism: string;
  dimensionSocialism: string;
  dimensionDogma: string;
  dimensionScience: string;
  dimensionAutocracy: string;
  dimensionDemocracy: string;
  dimensionConservatism: string;
  dimensionProgressivism: string;
  dimensionContent: string;
  dimensionDiscontent: string;
  stanceLabel: string;
  importanceLabel: string;
  submitTeabateAction: string;
  responseStronglyDisagree: string;
  responseDisagree: string;
  responseNeutral: string;
  responseAgree: string;
  responseStronglyAgree: string;
  importanceVeryLow: string;
  importanceLow: string;
  importanceNeutral: string;
  importanceHigh: string;
  importanceVeryHigh: string;
  dimensionRequiredMessage: string;
  part2GuessesTitle: string;
  part2GuessesDescription: string;
  guessOptionsTitle: string;
  guessPrompt: string;
  guessPromptGeneric: string;
  submitGuessesAction: string;
  noTargetsTitle: string;
  part2ResponsesTitle: string;
  part2ResponsesDescription: string;
  selectedByCountLabel: string;
  submitResponsesAction: string;
  noTeabatesTitle: string;
  waitingTitle: string;
  waitingForOthersDescription: string;
  waitingForCreatorDescription: string;
  creatorReadyPart2Title: string;
  creatorReadyPart2Description: string;
  creatorReadyPart2NeedsOtherComplete: string;
  startPart2Action: string;
  viewQuizAction: string;
  endQuizAction: string;
  endQuizNeedsOtherComplete: string;
  endQuizConfirmTitle: string;
  endQuizConfirmDescription: string;
  endQuizConfirmAction: string;
  endQuizCancelAction: string;
  viewResultsAction: string;
  part2LockedTitle: string;
  part2LockedDescription: string;
  resultsTitle: string;
  resultsDescription: string;
  scoreboardTitle: string;
  overallAlignmentTitle: string;
  alignmentQuizOnly: string;
  alignmentOverall: string;
  catchUpAction: string;
  perPlayerResultsTitle: string;
  thisPlayerLabel: string;
  otherPlayersLabel: string;
  topIssuesTitle: string;
  chosenTeabateTitle: string;
  responsesTitle: string;
  publishToProfileAction: string;
  publishedLabel: string;
  readyLabel: string;
  scoreLabel: string;
  noAlignmentData: string;
  pushBannerTitle: string;
  pushBannerDescription: string;
  pushBannerAction: string;
  dismissPushBannerAction: string;
}

const STRINGS: Record<QuizLocale, QuizUiStrings> = {
  "en-US": {
    pageTitle: "Societea Challenge",
    loadingQuiz: "Loading quiz...",
    genericLoadError: "We couldn't load this quiz right now.",
    genericSaveError: "We couldn't save that step. Please try again.",
    genericPublishError: "We couldn't publish your quiz content right now.",
    genericPublishSuccess: "Your quiz content was published to your profile.",
    genericCopied: "Copied to clipboard.",
    landingTitle: "Societea Challenge",
    landingDescription: "Start a live quiz with friends to discover which issues matter most and how your perspectives line up.",
    startNewQuiz: "Start a quiz",
    startNewQuizBody: "Create a room, share the code, and lead everyone into the challenge.",
    sipTogetherAction: "Sip together",
    sipTogetherDescription: "Start a live Societea Challenge with friends, family, or people you meet in the field.",
    enterCodeTitle: "Join with a code",
    enterCodeBody: "Enter a short code from a friend or scan their QR link to join the same quiz.",
    enterCodeLabel: "Quiz code",
    enterCodePlaceholder: "Enter code",
    createQuizAction: "Create quiz",
    joinQuizAction: "Join quiz",
    signInToCreate: "Sign in to create",
    signInToJoin: "Sign in to join",
    authContinueTitle: "Sign in to continue",
    authContinueBody: "This quiz saves your responses and compares them with everyone else in the room.",
    joinHostedByLabel: "Created by",
    joinClosed: "This quiz is no longer accepting new players.",
    invalidCode: "Enter a valid quiz code.",
    lobbyTitle: "Invite your players",
    lobbyDescription: "Share the code or QR link, then start when your group is ready.",
    codeLabel: "Quiz code",
    inviteEmailsTitle: "Invite by email",
    inviteEmailsDescription: "Add one or more email addresses and we'll send a quiz invite from your Societea profile.",
    inviteEmailsLabel: "Email addresses",
    inviteEmailsPlaceholder: "Enter an email and press Enter",
    inviteEmailsSendAction: "Send invites",
    inviteEmailsInvalid: "Enter a valid email address.",
    inviteEmailsTooMany: "You can send up to 20 invites at once.",
    inviteEmailsSuccess: "Quiz invites sent.",
    inviteEmailsPartial: "Some invites were sent, but a few addresses failed.",
    joinedPlayersTitle: "Players",
    shareHeading: "Share invite",
    shareTitle: "Join my quiz on Societea",
    shareText: "Let's learn more about what issues are important to us and how we feel about them",
    copyCodeAction: "Copy code",
    startLobbyAction: "Start",
    startNeedsOtherPlayer: "At least one other player must join before you can start.",
    waitingForCreator: "Waiting for the creator to begin.",
    noPlayersYet: "No players have joined yet.",
    part1IssuesTitle: "Part 1: Your top issues",
    part1IssuesDescription: "Pick and rank your top 3 issues, then mark whether you feel positive, neutral, or negative about each one.",
    issueModalActionLabel: "Continue",
    issueModalSubscribeLabel: "Subscribe",
    issueModalSubscribeDescription: "Automatically subscribe so these issues keep shaping your Societea profile and notifications.",
    issueModalEmptyTitle: "No issues ranked yet",
    issueModalEmptySubtitle: "Search and add issues below to get started.",
    searchIssuePlaceholder: "Search for an issue",
    selectedIssuesTitle: "Your ranked issues",
    noIssuesYet: "Add three issues to continue.",
    needThreeIssues: "You need exactly 3 issues before you can continue.",
    continueToTeabateAction: "Continue",
    continueToWaitingAction: "Done for now",
    sentimentNegative: "Negative",
    sentimentNeutral: "Neutral",
    sentimentPositive: "Positive",
    removeIssueAction: "Remove issue",
    part1TeabateTitle: "Part 1: Choose a teabate",
    part1TeabateDescription: "Choose one of your ranked issues, then either pick an existing teabate with a dimension or create a new one.",
    perspectiveCreateButtonText: "Start a Teabate",
    perspectiveCreateButtonTooltip: "Create a new teabate",
    perspectiveCloseLabel: "Close",
    selectIssueFirstText: "Choose one of your ranked issues to browse or create a teabate.",
    chooseRankedIssueLabel: "Issue for this teabate",
    issuePickerNoResults: "No ranked issues match that search.",
    selectedTeabatesTitle: "Your quiz responses",
    existingTeabatesTitle: "Existing teabates",
    existingTeabatesHelp: "Pick a dimensioned teabate for this issue, or create your own claim below.",
    noExistingTeabates: "No dimensioned teabates yet for this issue.",
    needAtLeastOneTeabate: "Choose at least one teabate before continuing.",
    maxTeabatesReached: "You can save up to five teabates for this quiz.",
    addMoreTeabatesAction: "Add more responses",
    createNewTeabateAction: "Create a new teabate",
    useSelectedTeabateAction: "Use this teabate",
    createClaimLabel: "Claim",
    createArgumentLabel: "Argument",
    createDimensionLabel: "Societal dimension",
    createDimensionPlaceholder: "Select a societal dimension",
    createAgreeLabel: "Agree",
    createDisagreeLabel: "Disagree",
    dimensionNationalism: "Nationalism",
    dimensionCosmopolitanism: "Cosmopolitanism",
    dimensionTheocracy: "Theocracy",
    dimensionSecularism: "Secularism",
    dimensionAuthority: "Authority",
    dimensionLiberty: "Liberty",
    dimensionCapitalism: "Capitalism",
    dimensionSocialism: "Socialism",
    dimensionDogma: "Dogma",
    dimensionScience: "Science",
    dimensionAutocracy: "Autocracy",
    dimensionDemocracy: "Democracy",
    dimensionConservatism: "Conservatism",
    dimensionProgressivism: "Progressivism",
    dimensionContent: "Status quo",
    dimensionDiscontent: "Reformation",
    stanceLabel: "Your stance",
    importanceLabel: "Importance",
    submitTeabateAction: "Save teabate",
    responseStronglyDisagree: "Strongly disagree",
    responseDisagree: "Disagree",
    responseNeutral: "Neutral",
    responseAgree: "Agree",
    responseStronglyAgree: "Strongly agree",
    importanceVeryLow: "Very unimportant",
    importanceLow: "Unimportant",
    importanceNeutral: "Neutral",
    importanceHigh: "Important",
    importanceVeryHigh: "Very important",
    dimensionRequiredMessage: "Choose a societal dimension before starting a teabate.",
    part2GuessesTitle: "Part 2: Guess the top issue",
    part2GuessesDescription: "Look at each other player and guess which issue they ranked #1.",
    guessOptionsTitle: "Issue options",
    guessPrompt: "Which issue did {name} rank #1?",
    guessPromptGeneric: "Which issue did this player rank #1?",
    submitGuessesAction: "Save guesses",
    noTargetsTitle: "No other active players to guess yet.",
    part2ResponsesTitle: "Part 2: Respond to the teabates",
    part2ResponsesDescription: "Respond to the anonymous teabates chosen by the other players.",
    selectedByCountLabel: "{count} players picked this teabate",
    submitResponsesAction: "Save responses",
    noTeabatesTitle: "No teabates from other players are available.",
    waitingTitle: "Waiting room",
    waitingForOthersDescription: "You're done for now. Waiting for the other players to finish.",
    waitingForCreatorDescription: "You're done for now. Waiting for the creator to move the quiz forward.",
    creatorReadyPart2Title: "Ready for part 2",
    creatorReadyPart2Description: "You can move the group into the main guessing round whenever you want.",
    creatorReadyPart2NeedsOtherComplete: "At least one other player must finish part 1 before part 2 can start.",
    startPart2Action: "Start part 2",
    viewQuizAction: "View quiz",
    endQuizAction: "End quiz",
    endQuizNeedsOtherComplete: "At least one other player must finish the quiz before you can end it.",
    endQuizConfirmTitle: "End quiz?",
    endQuizConfirmDescription: "This will prevent any new participants from joining. Proceed?",
    endQuizConfirmAction: "Yes, end quiz",
    endQuizCancelAction: "No",
    viewResultsAction: "View results",
    part2LockedTitle: "Part 2 started without you",
    part2LockedDescription: "The creator moved on before you finished part 1. You can still watch the results when the quiz ends.",
    resultsTitle: "Results",
    resultsDescription: "See who guessed best and how your overall alignment compares with everyone else.",
    scoreboardTitle: "Scoreboard",
    overallAlignmentTitle: "Overall alignment",
    alignmentQuizOnly: "Quiz only",
    alignmentOverall: "Overall",
    catchUpAction: "Respond now",
    perPlayerResultsTitle: "Player results",
    thisPlayerLabel: "this player",
    otherPlayersLabel: "everyone else",
    topIssuesTitle: "Top issues",
    chosenTeabateTitle: "Chosen teabate",
    responsesTitle: "Responses",
    publishToProfileAction: "Publish to profile",
    publishedLabel: "Published",
    readyLabel: "Ready",
    scoreLabel: "{correct} / {total} correct",
    noAlignmentData: "Not enough alignment data yet.",
    pushBannerTitle: "Turn on push notifications",
    pushBannerDescription: "Get notified when players finish the quiz and when everyone currently active is done.",
    pushBannerAction: "Enable",
    dismissPushBannerAction: "Dismiss push notification banner",
  },
  "ja-JP": {
    pageTitle: "Societea Challenge",
    loadingQuiz: "クイズを読み込み中...",
    genericLoadError: "今はこのクイズを読み込めません。",
    genericSaveError: "このステップを保存できませんでした。もう一度お試しください。",
    genericPublishError: "今はクイズ内容をプロフィールに公開できません。",
    genericPublishSuccess: "クイズ内容をプロフィールに公開しました。",
    genericCopied: "クリップボードにコピーしました。",
    landingTitle: "Societea Challenge",
    landingDescription: "友だちとライブクイズを始めて、大切にしている課題や視点の近さを見つけましょう。",
    startNewQuiz: "クイズを始める",
    startNewQuizBody: "ルームを作成し、コードを共有して、みんなをチャレンジへ招待します。",
    sipTogetherAction: "一緒にひとくち",
    sipTogetherDescription: "友だちや家族、現場で出会った人たちと Societea Challenge を始めましょう。",
    enterCodeTitle: "コードで参加",
    enterCodeBody: "友だちから受け取った短いコードを入力するか、QR リンクを読み取って同じクイズに参加します。",
    enterCodeLabel: "クイズコード",
    enterCodePlaceholder: "コードを入力",
    createQuizAction: "クイズを作成",
    joinQuizAction: "参加する",
    signInToCreate: "サインインして作成",
    signInToJoin: "サインインして参加",
    authContinueTitle: "続けるにはサインインしてください",
    authContinueBody: "このクイズでは回答を保存し、同じルームのみんなと比較します。",
    joinHostedByLabel: "作成者",
    joinClosed: "このクイズは新しい参加者を受け付けていません。",
    invalidCode: "有効なクイズコードを入力してください。",
    lobbyTitle: "参加者を招待",
    lobbyDescription: "コードまたは QR リンクを共有し、準備ができたら開始してください。",
    codeLabel: "クイズコード",
    inviteEmailsTitle: "メールで招待",
    inviteEmailsDescription: "メールアドレスを追加すると、あなたの Societea プロフィールからクイズ招待を送信します。",
    inviteEmailsLabel: "メールアドレス",
    inviteEmailsPlaceholder: "メールアドレスを入力して Enter",
    inviteEmailsSendAction: "招待を送信",
    inviteEmailsInvalid: "有効なメールアドレスを入力してください。",
    inviteEmailsTooMany: "一度に送れる招待は 20 件までです。",
    inviteEmailsSuccess: "クイズ招待を送信しました。",
    inviteEmailsPartial: "一部の招待は送信できませんでした。",
    joinedPlayersTitle: "参加者",
    shareHeading: "招待を共有",
    shareTitle: "Societea のクイズに参加しよう",
    shareText: "私たちにとって大切な課題や、その感じ方をもっと知ろう",
    copyCodeAction: "コードをコピー",
    startLobbyAction: "開始",
    startNeedsOtherPlayer: "開始するには、少なくとも他に 1 人の参加者が必要です。",
    waitingForCreator: "作成者が開始するのを待っています。",
    noPlayersYet: "まだ参加者はいません。",
    part1IssuesTitle: "パート 1: 大切な課題",
    part1IssuesDescription: "大切な課題を 3 つ選んで順位づけし、それぞれに対して前向き・中立・否定的のどれかを選んでください。",
    issueModalActionLabel: "続ける",
    issueModalSubscribeLabel: "フォロー",
    issueModalSubscribeDescription: "これらの課題をプロフィールや通知に反映し続けるため、自動でフォローします。",
    issueModalEmptyTitle: "まだ課題がありません",
    issueModalEmptySubtitle: "下で検索して課題を追加してください。",
    searchIssuePlaceholder: "課題を検索",
    selectedIssuesTitle: "順位づけした課題",
    noIssuesYet: "続けるには 3 つの課題を追加してください。",
    needThreeIssues: "続けるにはちょうど 3 つの課題が必要です。",
    continueToTeabateAction: "続ける",
    continueToWaitingAction: "ここまでにする",
    sentimentNegative: "否定的",
    sentimentNeutral: "中立",
    sentimentPositive: "前向き",
    removeIssueAction: "課題を削除",
    part1TeabateTitle: "パート 1: ティーベートを選ぶ",
    part1TeabateDescription: "順位づけした課題の 1 つを選び、その課題の既存ティーベートを使うか、新しく作成してください。",
    perspectiveCreateButtonText: "ティーベートを始める",
    perspectiveCreateButtonTooltip: "新しいティーベートを作成",
    perspectiveCloseLabel: "閉じる",
    selectIssueFirstText: "順位づけした課題から 1 つ選ぶと、ティーベートを選んだり作成したりできます。",
    chooseRankedIssueLabel: "このティーベートに使う課題",
    issuePickerNoResults: "検索条件に一致する順位づけ済みの課題がありません。",
    selectedTeabatesTitle: "このクイズで選んだ回答",
    existingTeabatesTitle: "既存のティーベート",
    existingTeabatesHelp: "この課題の次元付きティーベートを選ぶか、下で自分の主張を作成してください。",
    noExistingTeabates: "この課題には次元付きティーベートがまだありません。",
    needAtLeastOneTeabate: "続けるには少なくとも 1 つのティーベートを選んでください。",
    maxTeabatesReached: "このクイズでは最大 5 つのティーベートを保存できます。",
    addMoreTeabatesAction: "回答をさらに追加",
    createNewTeabateAction: "新しいティーベートを作成",
    useSelectedTeabateAction: "このティーベートを使う",
    createClaimLabel: "主張",
    createArgumentLabel: "理由",
    createDimensionLabel: "社会的次元",
    createDimensionPlaceholder: "社会的次元を選択",
    createAgreeLabel: "賛成",
    createDisagreeLabel: "反対",
    dimensionNationalism: "ナショナリズム",
    dimensionCosmopolitanism: "コスモポリタニズム",
    dimensionTheocracy: "神権主義",
    dimensionSecularism: "世俗主義",
    dimensionAuthority: "権威",
    dimensionLiberty: "自由",
    dimensionCapitalism: "資本主義",
    dimensionSocialism: "社会主義",
    dimensionDogma: "ドグマ",
    dimensionScience: "科学",
    dimensionAutocracy: "専制政治",
    dimensionDemocracy: "民主主義",
    dimensionConservatism: "保守主義",
    dimensionProgressivism: "進歩主義",
    dimensionContent: "現状維持",
    dimensionDiscontent: "改革",
    stanceLabel: "あなたの立場",
    importanceLabel: "重要度",
    submitTeabateAction: "ティーベートを保存",
    responseStronglyDisagree: "強く反対",
    responseDisagree: "反対",
    responseNeutral: "中立",
    responseAgree: "賛成",
    responseStronglyAgree: "強く賛成",
    importanceVeryLow: "とても低い",
    importanceLow: "低い",
    importanceNeutral: "中立",
    importanceHigh: "高い",
    importanceVeryHigh: "とても高い",
    dimensionRequiredMessage: "ティーベートを始める前に社会的次元を選択してください。",
    part2GuessesTitle: "パート 2: 1 位の課題を当てる",
    part2GuessesDescription: "他の参加者ごとに、その人が 1 位にした課題を予想してください。",
    guessOptionsTitle: "課題の候補",
    guessPrompt: "{name} さんが 1 位にした課題はどれですか？",
    guessPromptGeneric: "この参加者が 1 位にした課題はどれですか？",
    submitGuessesAction: "予想を保存",
    noTargetsTitle: "予想する他の参加者がまだいません。",
    part2ResponsesTitle: "パート 2: ティーベートに回答",
    part2ResponsesDescription: "他の参加者が選んだ匿名ティーベートに回答してください。",
    selectedByCountLabel: "{count} 人がこのティーベートを選びました",
    submitResponsesAction: "回答を保存",
    noTeabatesTitle: "他の参加者のティーベートはまだありません。",
    waitingTitle: "待機中",
    waitingForOthersDescription: "今はここまでです。他の参加者が終わるのを待っています。",
    waitingForCreatorDescription: "今はここまでです。作成者が次に進めるのを待っています。",
    creatorReadyPart2Title: "パート 2 の準備完了",
    creatorReadyPart2Description: "好きなタイミングでみんなを予想ラウンドに進められます。",
    creatorReadyPart2NeedsOtherComplete: "パート 2 を始めるには、少なくとも他の 1 人がパート 1 を終える必要があります。",
    startPart2Action: "パート 2 を開始",
    viewQuizAction: "クイズを見る",
    endQuizAction: "クイズを終了",
    endQuizNeedsOtherComplete: "終了するには、少なくとも他の 1 人がクイズを完了している必要があります。",
    endQuizConfirmTitle: "クイズを終了しますか？",
    endQuizConfirmDescription: "終了すると、新しい参加者は参加できなくなります。続けますか？",
    endQuizConfirmAction: "はい、終了します",
    endQuizCancelAction: "いいえ",
    viewResultsAction: "結果を見る",
    part2LockedTitle: "パート 2 が先に始まりました",
    part2LockedDescription: "パート 1 を終える前に作成者が先へ進みました。クイズ終了後に結果は見られます。",
    resultsTitle: "結果",
    resultsDescription: "誰が一番当てたか、そして全体としてどれだけ考えが近いかを確認しましょう。",
    scoreboardTitle: "スコアボード",
    overallAlignmentTitle: "全体のアラインメント",
    alignmentQuizOnly: "クイズのみ",
    alignmentOverall: "全体",
    catchUpAction: "今すぐ回答",
    perPlayerResultsTitle: "参加者ごとの結果",
    thisPlayerLabel: "この参加者",
    otherPlayersLabel: "他のみんな",
    topIssuesTitle: "上位の課題",
    chosenTeabateTitle: "選んだティーベート",
    responsesTitle: "回答",
    publishToProfileAction: "プロフィールに公開",
    publishedLabel: "公開済み",
    readyLabel: "準備完了",
    scoreLabel: "{correct} / {total} 正解",
    noAlignmentData: "アラインメントの比較に十分なデータがありません。",
    pushBannerTitle: "プッシュ通知を有効にする",
    pushBannerDescription: "参加者が完了したときや、現在アクティブな全員が完了したときに通知を受け取りましょう。",
    pushBannerAction: "有効にする",
    dismissPushBannerAction: "プッシュ通知バナーを閉じる",
  },
  "ko-KR": {
    pageTitle: "Societea Challenge",
    loadingQuiz: "퀴즈를 불러오는 중...",
    genericLoadError: "지금은 이 퀴즈를 불러올 수 없습니다.",
    genericSaveError: "이 단계를 저장하지 못했습니다. 다시 시도해 주세요.",
    genericPublishError: "지금은 퀴즈 내용을 프로필에 공개할 수 없습니다.",
    genericPublishSuccess: "퀴즈 내용을 프로필에 공개했습니다.",
    genericCopied: "클립보드에 복사했습니다.",
    landingTitle: "Societea Challenge",
    landingDescription: "친구들과 실시간 퀴즈를 시작해 무엇이 가장 중요한 이슈인지, 서로의 관점이 얼마나 가까운지 알아보세요.",
    startNewQuiz: "퀴즈 시작",
    startNewQuizBody: "방을 만들고 코드를 공유한 뒤, 모두를 챌린지로 이끌어 보세요.",
    sipTogetherAction: "함께 한 모금",
    sipTogetherDescription: "친구, 가족, 혹은 현장에서 만난 사람들과 Societea Challenge를 시작해 보세요.",
    enterCodeTitle: "코드로 참가",
    enterCodeBody: "친구에게 받은 짧은 코드를 입력하거나 QR 링크를 스캔해 같은 퀴즈에 참가하세요.",
    enterCodeLabel: "퀴즈 코드",
    enterCodePlaceholder: "코드 입력",
    createQuizAction: "퀴즈 만들기",
    joinQuizAction: "참가하기",
    signInToCreate: "로그인 후 만들기",
    signInToJoin: "로그인 후 참가",
    authContinueTitle: "계속하려면 로그인하세요",
    authContinueBody: "이 퀴즈는 응답을 저장하고 같은 방의 다른 사람들과 비교합니다.",
    joinHostedByLabel: "만든 사람",
    joinClosed: "이 퀴즈는 더 이상 새 참가자를 받지 않습니다.",
    invalidCode: "유효한 퀴즈 코드를 입력하세요.",
    lobbyTitle: "참가자 초대",
    lobbyDescription: "코드나 QR 링크를 공유하고, 준비가 되면 시작하세요.",
    codeLabel: "퀴즈 코드",
    inviteEmailsTitle: "이메일로 초대",
    inviteEmailsDescription: "이메일 주소를 추가하면 Societea 프로필 이름으로 퀴즈 초대를 보냅니다.",
    inviteEmailsLabel: "이메일 주소",
    inviteEmailsPlaceholder: "이메일을 입력하고 Enter",
    inviteEmailsSendAction: "초대 보내기",
    inviteEmailsInvalid: "유효한 이메일 주소를 입력하세요.",
    inviteEmailsTooMany: "한 번에 최대 20명까지 초대할 수 있습니다.",
    inviteEmailsSuccess: "퀴즈 초대를 보냈습니다.",
    inviteEmailsPartial: "일부 초대는 보내졌지만 몇 개는 실패했습니다.",
    joinedPlayersTitle: "참가자",
    shareHeading: "초대 공유",
    shareTitle: "Societea 퀴즈에 함께해요",
    shareText: "우리에게 중요한 이슈와 그에 대한 감정을 더 알아보자",
    copyCodeAction: "코드 복사",
    startLobbyAction: "시작",
    startNeedsOtherPlayer: "시작하려면 최소 한 명 이상의 다른 참가자가 필요합니다.",
    waitingForCreator: "만든 사람이 시작하기를 기다리는 중입니다.",
    noPlayersYet: "아직 참가자가 없습니다.",
    part1IssuesTitle: "1부: 나의 핵심 이슈",
    part1IssuesDescription: "가장 중요한 이슈 3개를 고르고 순위를 매긴 뒤, 각 이슈에 대해 긍정·중립·부정 중 하나를 선택하세요.",
    issueModalActionLabel: "계속",
    issueModalSubscribeLabel: "구독",
    issueModalSubscribeDescription: "이 이슈들이 프로필과 알림에 계속 반영되도록 자동으로 구독합니다.",
    issueModalEmptyTitle: "아직 선택한 이슈가 없습니다",
    issueModalEmptySubtitle: "아래에서 검색해 이슈를 추가해 보세요.",
    searchIssuePlaceholder: "이슈 검색",
    selectedIssuesTitle: "순위를 매긴 이슈",
    noIssuesYet: "계속하려면 이슈 3개를 추가하세요.",
    needThreeIssues: "계속하려면 정확히 3개의 이슈가 필요합니다.",
    continueToTeabateAction: "계속",
    continueToWaitingAction: "여기까지 하기",
    sentimentNegative: "부정적",
    sentimentNeutral: "중립",
    sentimentPositive: "긍정적",
    removeIssueAction: "이슈 제거",
    part1TeabateTitle: "1부: 티베이트 선택",
    part1TeabateDescription: "순위를 매긴 이슈 중 하나를 고르고, 기존 티베이트를 선택하거나 새로 만들어 보세요.",
    perspectiveCreateButtonText: "티베이트 시작",
    perspectiveCreateButtonTooltip: "새 티베이트 만들기",
    perspectiveCloseLabel: "닫기",
    selectIssueFirstText: "순위를 매긴 이슈 중 하나를 선택하면 티베이트를 고르거나 새로 만들 수 있습니다.",
    chooseRankedIssueLabel: "이 티베이트에 사용할 이슈",
    issuePickerNoResults: "검색과 일치하는 순위 이슈가 없습니다.",
    selectedTeabatesTitle: "이 퀴즈에서 저장한 응답",
    existingTeabatesTitle: "기존 티베이트",
    existingTeabatesHelp: "이 이슈의 차원 지정 티베이트를 고르거나 아래에서 직접 새 주장을 만드세요.",
    noExistingTeabates: "이 이슈에는 아직 차원 지정 티베이트가 없습니다.",
    needAtLeastOneTeabate: "계속하려면 최소 한 개의 티베이트를 선택하세요.",
    maxTeabatesReached: "이 퀴즈에서는 최대 다섯 개의 티베이트를 저장할 수 있습니다.",
    addMoreTeabatesAction: "응답 더 추가",
    createNewTeabateAction: "새 티베이트 만들기",
    useSelectedTeabateAction: "이 티베이트 사용",
    createClaimLabel: "주장",
    createArgumentLabel: "근거",
    createDimensionLabel: "사회적 차원",
    createDimensionPlaceholder: "사회적 차원 선택",
    createAgreeLabel: "찬성",
    createDisagreeLabel: "반대",
    dimensionNationalism: "민족주의",
    dimensionCosmopolitanism: "세계주의",
    dimensionTheocracy: "신정주의",
    dimensionSecularism: "세속주의",
    dimensionAuthority: "권위",
    dimensionLiberty: "자유",
    dimensionCapitalism: "자본주의",
    dimensionSocialism: "사회주의",
    dimensionDogma: "교조주의",
    dimensionScience: "과학",
    dimensionAutocracy: "전제정",
    dimensionDemocracy: "민주주의",
    dimensionConservatism: "보수주의",
    dimensionProgressivism: "진보주의",
    dimensionContent: "현상 유지",
    dimensionDiscontent: "개혁",
    stanceLabel: "내 입장",
    importanceLabel: "중요도",
    submitTeabateAction: "티베이트 저장",
    responseStronglyDisagree: "매우 반대",
    responseDisagree: "반대",
    responseNeutral: "중립",
    responseAgree: "찬성",
    responseStronglyAgree: "매우 찬성",
    importanceVeryLow: "매우 낮음",
    importanceLow: "낮음",
    importanceNeutral: "중립",
    importanceHigh: "높음",
    importanceVeryHigh: "매우 높음",
    dimensionRequiredMessage: "티베이트를 시작하기 전에 사회적 차원을 선택하세요.",
    part2GuessesTitle: "2부: 1순위 이슈 맞히기",
    part2GuessesDescription: "다른 참가자마다 그 사람이 1순위로 고른 이슈를 맞혀 보세요.",
    guessOptionsTitle: "이슈 선택지",
    guessPrompt: "{name} 님의 1순위 이슈는 무엇일까요?",
    guessPromptGeneric: "이 플레이어의 1순위 이슈는 무엇일까요?",
    submitGuessesAction: "추측 저장",
    noTargetsTitle: "아직 추측할 다른 활성 참가자가 없습니다.",
    part2ResponsesTitle: "2부: 티베이트에 응답하기",
    part2ResponsesDescription: "다른 참가자들이 고른 익명 티베이트에 응답하세요.",
    selectedByCountLabel: "{count}명이 이 티베이트를 선택했습니다",
    submitResponsesAction: "응답 저장",
    noTeabatesTitle: "다른 참가자의 티베이트가 아직 없습니다.",
    waitingTitle: "대기 중",
    waitingForOthersDescription: "지금은 여기까지입니다. 다른 참가자들이 끝나기를 기다리고 있습니다.",
    waitingForCreatorDescription: "지금은 여기까지입니다. 만든 사람이 다음 단계로 넘기기를 기다리고 있습니다.",
    creatorReadyPart2Title: "2부 시작 준비 완료",
    creatorReadyPart2Description: "원할 때 그룹을 메인 추측 라운드로 이동시킬 수 있습니다.",
    creatorReadyPart2NeedsOtherComplete: "2부를 시작하려면 다른 플레이어 중 최소 한 명이 1부를 끝내야 합니다.",
    startPart2Action: "2부 시작",
    viewQuizAction: "퀴즈 보기",
    endQuizAction: "퀴즈 종료",
    endQuizNeedsOtherComplete: "퀴즈를 종료하려면 다른 플레이어 중 최소 한 명이 완료해야 합니다.",
    endQuizConfirmTitle: "퀴즈를 종료할까요?",
    endQuizConfirmDescription: "종료하면 새 참가자는 더 이상 참가할 수 없습니다. 계속할까요?",
    endQuizConfirmAction: "예, 종료합니다",
    endQuizCancelAction: "아니요",
    viewResultsAction: "결과 보기",
    part2LockedTitle: "2부가 먼저 시작되었습니다",
    part2LockedDescription: "1부를 끝내기 전에 만든 사람이 다음 단계로 넘어갔습니다. 퀴즈가 끝나면 결과는 볼 수 있습니다.",
    resultsTitle: "결과",
    resultsDescription: "누가 가장 잘 맞혔는지, 그리고 전체적으로 얼마나 비슷한지 확인해 보세요.",
    scoreboardTitle: "점수판",
    overallAlignmentTitle: "전체 정렬도",
    alignmentQuizOnly: "퀴즈만",
    alignmentOverall: "전체",
    catchUpAction: "지금 응답하기",
    perPlayerResultsTitle: "플레이어별 결과",
    thisPlayerLabel: "이 플레이어",
    otherPlayersLabel: "다른 모든 사람",
    topIssuesTitle: "상위 이슈",
    chosenTeabateTitle: "선택한 티베이트",
    responsesTitle: "응답",
    publishToProfileAction: "프로필에 공개",
    publishedLabel: "공개됨",
    readyLabel: "준비됨",
    scoreLabel: "{correct} / {total} 정답",
    noAlignmentData: "정렬도를 비교하기에 충분한 데이터가 없습니다.",
    pushBannerTitle: "푸시 알림 켜기",
    pushBannerDescription: "플레이어가 퀴즈를 끝냈을 때와 현재 활성 참가자가 모두 끝났을 때 알림을 받으세요.",
    pushBannerAction: "활성화",
    dismissPushBannerAction: "푸시 알림 배너 닫기",
  },
};

export function resolveQuizLocale(): QuizLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }
  const normalized = navigator.language.toLowerCase();
  if (normalized.startsWith("ja")) {
    return "ja-JP";
  }
  if (normalized.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

export function getQuizUiStrings(locale?: QuizLocale): QuizUiStrings {
  return STRINGS[locale || resolveQuizLocale()] || STRINGS["en-US"];
}
